<template>
  <div>
    <div
      v-if="shouldRenderModal"
      v-show="!showOverlay"
      id="registrationModal"
      ref="modalRef"
      class="modal fade"
      tabindex="-1"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content border-0 rounded-3 h-90vh">
          <!-- 固定ヘッダー -->
          <div class="flex-shrink-0 bg-blue-secondary py-2r">
            <h2 class="text-center size-16 fw-bold mb-0">
              求人へ応募するための必須項目を
              <br class="d-sm-none" />
              入力しましょう
            </h2>
          </div>

          <!-- スクロール可能な本体 -->
          <div
            ref="modalBodyRef"
            class="modal-body pt-0 px-0 pb-0 flex-grow-1 overflow-hidden d-flex flex-column transition-height"
            style="min-height: 0"
          >
            <!-- ステップタイトル -->
            <div class="pt-4 flex-shrink-0">
              <h3 class="text-center size-16 fw-bold mb-0">{{ stepTitle }}</h3>
              <div id="registration-step-message" class="mt-3 px-4 px-md-5"></div>
            </div>

            <!-- 入力エリア -->
            <div class="flex-grow-1 overflow-auto" style="min-height: 0">
              <transition
                :name="transitionName"
                mode="out-in"
                @before-leave="onBeforeLeave"
                @enter="onEnter"
                @after-enter="onAfterEnter"
              >
                <keep-alive>
                  <component
                    :is="currentStepComponent"
                    class="h-100 d-flex flex-column"
                    style="min-height: 0"
                    @next="nextStep"
                    @back="prevStep"
                    @open-direct-apply="openDirectApplyModal"
                    @open-profile-guide="openProfileGuideModal"
                    @open-eeasy-schedule="openEeasyScheduleModal"
                    @open-platform-direct="openPlatformDirectModal"
                  />
                </keep-alive>
              </transition>
            </div>
          </div>

          <!-- フッターをテレポートする領域 -->
          <div
            id="registration-footer"
            class="modal__footer px-4 px-md-5 mt-4 row justify-content-center mb-4 flex-shrink-0 bg-white pt-3 border-top"
          ></div>
        </div>
      </div>
    </div>

    <DirectApplyModal
      v-if="showDirectApplyModal && directJob"
      :job="directJob"
      :is-applied="isDirectApplied"
      :is-applying="isDirectApplying"
      @apply="applyDirectJob"
      @close="closeDirectApplyModal"
    />
    <ProfileGuideModal
      v-if="showProfileGuideModal"
      @edit="openProfileGuide"
      @close="closeProfileGuideModal"
    />
    <EeasyScheduleModal
      v-if="showEeasyScheduleModal"
      @schedule="openEeasy"
      @close="closeEeasyScheduleModal"
    />
    <DirectPlatformModal v-if="showPlatformDirectModal" />
  </div>
</template>

<script setup lang="ts">
import { getJobDetail } from '@/apis/job.api';
import DirectPlatformModal from '@/components/common/PlatformModal/Direct.vue';
import { EDIT_PROFILE_PATH } from '@/constant/routers/path';
import request from '@/services/http.service';
import { useRegistrationMasterDataStore } from '@/stores/registration/RegistrationMasterData.store';
import { useRegistrationStepsStore } from '@/stores/registration/RegistrationSteps.store';
import type { RecentlyViewedJob } from '@/types/models/recentlyViewedJob';
import type { ModalConstructor, ModalInstance } from '@/utils/bootstrap';
import { getBootstrapModal } from '@/utils/bootstrap';
import { EEASY_URL } from '@/utils/constants';
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';

const DIRECT_APPLY_STORAGE_KEY = 'direct_apply_job';
const Step1 = defineAsyncComponent(() => import('./Step1BasicInfo.vue'));
const Step2 = defineAsyncComponent(() => import('./Step2BasicInfo.vue'));
const Step3 = defineAsyncComponent(() => import('./Step3Career.vue'));
const Step4 = defineAsyncComponent(() => import('./Step4DesiredCondition.vue'));
const DirectApplyModal = defineAsyncComponent(() => import('./overlays/DirectApplyModal.vue'));
const ProfileGuideModal = defineAsyncComponent(() => import('./overlays/ProfileGuideModal.vue'));
const EeasyScheduleModal = defineAsyncComponent(() => import('./overlays/EeasyScheduleModal.vue'));

const modalRef = ref<HTMLElement | null>(null);
const modalInstance = ref<ModalInstance | null>(null);
const currentStep = ref(1);
const transitionName = ref('slide-fade');
const registrationMasterDataStore = useRegistrationMasterDataStore();
const registrationStepsStore = useRegistrationStepsStore();
const shouldRenderModal = ref(true);
const showDirectApplyModal = ref(false);
const showProfileGuideModal = ref(false);
const showEeasyScheduleModal = ref(false);
const showPlatformDirectModal = ref(false);
const directJob = ref<RecentlyViewedJob | null>(null);
const isDirectApplying = ref(false);
const isDirectApplied = ref(false);
const platformDirectModalInstance = ref<ModalInstance | null>(null);

const stepTitle = computed(() => {
  switch (currentStep.value) {
    case 1:
      return '基本情報(1/2)';
    case 2:
      return '基本情報(2/2)';
    case 3:
      return 'ご経験について';
    case 4:
      return '希望条件';
    default:
      return '';
  }
});

const currentStepComponent = computed(() => {
  switch (currentStep.value) {
    case 1:
      return Step1;
    case 2:
      return Step2;
    case 3:
      return Step3;
    case 4:
      return Step4;
    default:
      return Step1;
  }
});

const showOverlay = computed(
  () =>
    showDirectApplyModal.value ||
    showProfileGuideModal.value ||
    showEeasyScheduleModal.value ||
    showPlatformDirectModal.value
);

onMounted(async () => {
  try {
    await registrationStepsStore.initialize();

    if (!registrationStepsStore.shouldDisplayModal) {
      shouldRenderModal.value = false;
      return;
    }

    await registrationMasterDataStore.loadStep1Data();
    await registrationMasterDataStore.loadStep2Data();

    if (modalRef.value) {
      const ModalCtor: ModalConstructor = getBootstrapModal();
      modalInstance.value = new ModalCtor(modalRef.value);
      modalInstance.value.show();
    }
  } catch (error) {
    console.error('[RegistrationModal] initialization failed', error);
    shouldRenderModal.value = false;
  }
});

onBeforeUnmount(() => {
  modalInstance.value?.dispose();
  modalInstance.value = null;
});

const nextStep = async () => {
  if (currentStep.value < 4) {
    transitionName.value = 'slide-fade';
    currentStep.value++;
    return;
  }
  await handleRegistrationCompleted();
};

const prevStep = () => {
  if (currentStep.value > 1) {
    transitionName.value = 'slide-fade-reverse';
    currentStep.value--;
  }
};

watch(
  () => currentStep.value,
  (step) => {
    if (step === 2) {
      registrationMasterDataStore.loadStep3Data();
    }
  },
  { immediate: true }
);

const onClose = () => {
  modalInstance.value?.hide();
};

const modalBodyRef = ref<HTMLElement | null>(null);

const onBeforeLeave = () => {
  if (modalBodyRef.value) {
    modalBodyRef.value.style.height = `${modalBodyRef.value.offsetHeight}px`;
  }
};

const onEnter = () => {
  if (modalBodyRef.value) {
    const oldHeight = modalBodyRef.value.style.height;

    // 新しい高さを計測
    modalBodyRef.value.style.height = 'auto';
    const newHeight = modalBodyRef.value.offsetHeight;

    // アニメーション開始のために旧高さへ戻す
    modalBodyRef.value.style.height = oldHeight;

    // 再描画を強制
    modalBodyRef.value.offsetHeight;

    // 計測した高さへアニメーション
    requestAnimationFrame(() => {
      if (modalBodyRef.value) {
        modalBodyRef.value.style.height = `${newHeight}px`;
      }
    });
  }
};

const onAfterEnter = () => {
  if (modalBodyRef.value) {
    modalBodyRef.value.style.height = '';
  }
};

const getDirectApplyJobFromStorage = (): RecentlyViewedJob | null => {
  try {
    const stored = localStorage.getItem(DIRECT_APPLY_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed?.jobId) {
      localStorage.removeItem(DIRECT_APPLY_STORAGE_KEY);
      return null;
    }
    return {
      platform: 'direct',
      jobId: parsed.jobId,
      title: parsed.jobTitle ?? parsed.title,
      companyName: parsed.companyName,
    };
  } catch (error) {
    console.error('[RegistrationModal] failed to parse direct apply context', error);
    localStorage.removeItem(DIRECT_APPLY_STORAGE_KEY);
    return null;
  }
};

const clearDirectApplyJobStorage = (): void => {
  try {
    localStorage.removeItem(DIRECT_APPLY_STORAGE_KEY);
  } catch {
    console.error('[RegistrationModal] failed to clear direct apply job storage');
  }
};

const handleRegistrationCompleted = async (): Promise<void> => {
  const directApplyJob = getDirectApplyJobFromStorage();
  onClose();
  if (directApplyJob) {
    clearDirectApplyJobStorage();
    await openDirectApplyModal(directApplyJob);
    return;
  }
  openProfileGuideModal();
};

const fetchJobSummary = async (
  jobId?: string | number | null
): Promise<Partial<RecentlyViewedJob>> => {
  if (jobId === undefined || jobId === null) return {};
  try {
    const res = await getJobDetail(String(jobId));
    return {
      companyName: res?.data?.company_name ?? undefined,
      title: res?.data?.title ?? undefined,
    };
  } catch (error) {
    console.error('[RegistrationModal] failed to fetch job summary', error);
    return {};
  }
};

const openDirectApplyModal = async (job: RecentlyViewedJob): Promise<void> => {
  const shouldFetchDetail = !job.companyName || !job.title;
  const jobSummary = shouldFetchDetail ? await fetchJobSummary(job.jobId) : {};
  directJob.value = { ...job, ...jobSummary };
  isDirectApplying.value = false;
  isDirectApplied.value = false;
  showDirectApplyModal.value = true;
  modalInstance.value?.hide();
};

const closeDirectApplyModal = (): void => {
  showDirectApplyModal.value = false;
  isDirectApplying.value = false;
  isDirectApplied.value = false;
};

const applyDirectJob = async (): Promise<void> => {
  if (!directJob.value?.jobId) {
    return;
  }
  try {
    isDirectApplying.value = true;
    const formData = new FormData();
    formData.append('propositions_job_applications_create_form[apply_message]', '');
    const res = await request(`/propositions/job_applications?job_id=${directJob.value.jobId}`, {
      method: 'POST',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (res && (res.status === 200 || res.data?.status === 200)) {
      isDirectApplied.value = true;
      return;
    }
  } catch (error) {
    console.error('[RegistrationModal] apply job failed', error);
  } finally {
    isDirectApplying.value = false;
  }
};

const openProfileGuideModal = (): void => {
  showProfileGuideModal.value = true;
  modalInstance.value?.hide();
};

const closeProfileGuideModal = (): void => {
  showProfileGuideModal.value = false;
};

const openProfileGuide = (): void => {
  window.location.href = EDIT_PROFILE_PATH;
};

const openEeasyScheduleModal = (): void => {
  showEeasyScheduleModal.value = true;
  modalInstance.value?.hide();
};

const closeEeasyScheduleModal = (): void => {
  showEeasyScheduleModal.value = false;
  onClose();
};

const openEeasy = (): void => {
  window.open(EEASY_URL, '_blank');
  closeEeasyScheduleModal();
};

const openPlatformDirectModal = async (): Promise<void> => {
  showPlatformDirectModal.value = true;
  await nextTick();
  const modalElement = document.getElementById('platformDirectModal');
  if (!modalElement) return;
  const ModalCtor: ModalConstructor = getBootstrapModal();
  platformDirectModalInstance.value = new ModalCtor(modalElement);
  modalInstance.value?.hide();
  platformDirectModalInstance.value.show();
  modalElement.addEventListener(
    'hidden.bs.modal',
    () => {
      showPlatformDirectModal.value = false;
      platformDirectModalInstance.value = null;
    },
    { once: true }
  );
};
</script>

<style lang="scss" scoped>
.transition-height {
  transition: height 0.5s ease-in-out;
}

.slide-fade-enter-active,
.slide-fade-leave-active,
.slide-fade-reverse-enter-active,
.slide-fade-reverse-leave-active {
  transition: all 0.3s ease-out;
}

.slide-fade-enter-from {
  transform: translateX(20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

.slide-fade-reverse-enter-from {
  transform: translateX(-20px);
  opacity: 0;
}

.slide-fade-reverse-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.modal-content {
  overflow: hidden; // アニメーションのはみ出し防止
}

.modal-body {
  overflow-x: hidden;
}

.h-90vh {
  height: 90vh;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

#registration-footer {
  min-height: 65px;
}

@media (min-width: 576px) and (max-width: 991.98px) {
  .modal-dialog {
    max-width: none;
    margin-left: 1.75rem;
    margin-right: 1.75rem;
  }
}

@media (min-width: 992px) {
  .modal-dialog {
    max-width: 960px;
  }
}
</style>
